/**
 * PreLaidTrackPanel - 敷設済み線路編集パネル
 *
 * Ph.4 の BuildTrackPhase から HexTileIcon を流用し、
 * エディタで敷設済み線路を配置・回転・確定できるUI
 */


// ============================================================================
// 型定義
// ============================================================================

/** 仮配置情報 */
interface TemporaryPlacement {
  q: number;
  r: number;
  tileType: string;
  rotation: number;
}

export interface PreLaidTrackPanelProps {
  /** 選択中のタイルタイプ */
  selectedTileType: string | null;
  /** 選択中の回転 */
  selectedRotation: number;
  /** タイルタイプ選択時のコールバック */
  onSelectTile: (tileType: string | null) => void;
  /** 回転変更時のコールバック */
  onRotate: () => void;
  /** 選択解除 */
  onClear: () => void;
  /** 仮配置情報（Ph.4スタイル） */
  temporaryPlacement?: TemporaryPlacement | null;
  /** 仮配置確定 */
  onConfirm?: () => void;
  /** 仮配置キャンセル */
  onCancel?: () => void;
}

// ============================================================================
// HexTileIcon（BuildTrackPhase.tsx から流用）
// ============================================================================

function getHexPoints(cx: number, cy: number, size: number): string {
  const points: string[] = [];
  for (let i = 0; i < 6; i++) {
    const compassAngle = (i * 60) * Math.PI / 180;
    const x = cx + size * Math.sin(compassAngle);
    const y = cy - size * Math.cos(compassAngle);
    points.push(`${x},${y}`);
  }
  return points.join(' ');
}

function getEdgeMidpoint(cx: number, cy: number, size: number, edge: number): { x: number; y: number } {
  const compassAngles = [30, 90, 150, 210, 270, 330];
  const θ = compassAngles[edge] * Math.PI / 180;
  const distance = size * Math.cos(Math.PI / 6);
  return {
    x: cx + distance * Math.sin(θ),
    y: cy - distance * Math.cos(θ),
  };
}

function HexTileIcon({ tileType, size = 48, rotation = 0 }: { tileType: string; size?: number; rotation?: number }) {
  const strokeColor = 'hsl(var(--foreground))';
  const trackColor = '#4A5568'; // 敷設済み線路は濃いグレー
  const strokeWidth = 2;
  const trackWidth = 4;
  const cx = size / 2;
  const cy = size / 2;
  const hexSize = size * 0.42;

  const getConnections = (): number[][] => {
    const connectionMap: Record<string, number[][]> = {
      'STRAIGHT': [[0, 3]],
      'ACUTE_CURVE': [[0, 1]],
      'GENTLE_CURVE': [[0, 2]],
      'GENTLE_STRAIGHT_CROSS': [[0, 3], [1, 5]],
      'TWO_GENTLE_CROSS': [[0, 2], [1, 3]],
      'TWO_STRAIGHT_CROSS': [[0, 3], [1, 4]],
      'TWO_GENTLE_COEXIST': [[0, 2], [3, 5]],
      'LEFT_GENTLE_ACUTE': [[0, 2], [3, 4]],
      'RIGHT_GENTLE_ACUTE': [[0, 1], [2, 4]],
      'STRAIGHT_ACUTE': [[0, 3], [4, 5]],
      'TOWN_1': [[0]],
      'TOWN_3_LEFT': [[0], [3], [4]],
      'TOWN_3_RIGHT': [[0], [2], [3]],
      'TOWN_3_HALF': [[0], [1], [2]],
      'TOWN_3_STAR': [[0], [2], [4]],
    };
    return connectionMap[tileType] || [];
  };

  const connections = getConnections();
  const isTownTile = tileType.startsWith('TOWN_');

  const renderTracks = () => {
    return connections.map((conn, idx) => {
      // 回転を適用
      const rotatedConn = conn.map(edge => (edge + rotation) % 6);

      if (rotatedConn.length === 1) {
        const edge = getEdgeMidpoint(cx, cy, hexSize, rotatedConn[0]);
        return (
          <line
            key={idx}
            x1={edge.x}
            y1={edge.y}
            x2={cx}
            y2={cy}
            stroke={trackColor}
            strokeWidth={trackWidth}
            strokeLinecap="round"
          />
        );
      } else if (rotatedConn.length === 2) {
        const edge1 = getEdgeMidpoint(cx, cy, hexSize, rotatedConn[0]);
        const edge2 = getEdgeMidpoint(cx, cy, hexSize, rotatedConn[1]);
        const diff = Math.abs(rotatedConn[0] - rotatedConn[1]);
        const isStraight = diff === 3;

        if (isStraight) {
          return (
            <line
              key={idx}
              x1={edge1.x}
              y1={edge1.y}
              x2={edge2.x}
              y2={edge2.y}
              stroke={trackColor}
              strokeWidth={trackWidth}
              strokeLinecap="round"
            />
          );
        } else {
          return (
            <path
              key={idx}
              d={`M ${edge1.x} ${edge1.y} Q ${cx} ${cy} ${edge2.x} ${edge2.y}`}
              fill="none"
              stroke={trackColor}
              strokeWidth={trackWidth}
              strokeLinecap="round"
            />
          );
        }
      }
      return null;
    });
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      <polygon
        points={getHexPoints(cx, cy, hexSize)}
        fill="hsl(var(--card))"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />
      {renderTracks()}
      {isTownTile && (
        <circle
          cx={cx}
          cy={cy}
          r={hexSize * 0.35}
          fill="white"
          stroke={strokeColor}
          strokeWidth={1.5}
        />
      )}
    </svg>
  );
}

// ============================================================================
// 敷設済み線路で使用可能なタイル
// ============================================================================

const AVAILABLE_TILES = [
  { type: 'STRAIGHT', name: 'Straight' },
  { type: 'GENTLE_CURVE', name: 'Gentle Curve' },
  { type: 'ACUTE_CURVE', name: 'Acute Curve' },
];

// ============================================================================
// PreLaidTrackPanel コンポーネント
// ============================================================================

export function PreLaidTrackPanel({
  selectedTileType,
  selectedRotation,
  onSelectTile,
  onRotate,
  onClear,
  temporaryPlacement,
  onConfirm,
  onCancel,
}: PreLaidTrackPanelProps) {
  const hasTemporary = temporaryPlacement !== null && temporaryPlacement !== undefined;

  return (
    <div className="flex flex-col gap-3 p-3 bg-card border border-border rounded-lg">
      <h4 className="text-sm font-semibold text-muted-foreground">Pre-laid Track</h4>

      {/* タイル選択 */}
      <div className="grid grid-cols-3 gap-2">
        {AVAILABLE_TILES.map((tile) => {
          const isSelected = selectedTileType === tile.type;
          return (
            <button
              key={tile.type}
              title={tile.name}
              className={`
                relative p-1.5 rounded-md border-2 transition-all aspect-square
                ${isSelected
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                  : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                }
              `}
              onClick={() => onSelectTile(isSelected ? null : tile.type)}
            >
              <HexTileIcon
                tileType={tile.type}
                size={44}
                rotation={isSelected ? selectedRotation : 0}
              />
            </button>
          );
        })}
      </div>

      {/* 仮配置中の情報 & 確定/キャンセル操作（Ph.4スタイル） */}
      {hasTemporary && (
        <div className="flex flex-col gap-2 p-2 bg-primary/10 border border-primary/30 rounded-md">
          <div className="text-xs text-center font-medium text-primary">
            Placing at ({temporaryPlacement.q}, {temporaryPlacement.r})
          </div>
          <div className="text-xs text-center text-muted-foreground">
            {temporaryPlacement.tileType} (rotation: {temporaryPlacement.rotation})
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              className="btn-secondary text-xs py-1.5"
              onClick={onRotate}
            >
              Rotate
            </button>
            <button
              className="btn-primary text-xs py-1.5"
              onClick={onConfirm}
            >
              Confirm
            </button>
            <button
              className="btn-ghost text-xs py-1.5"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Click hex to rotate. Confirm to place.
          </div>
        </div>
      )}

      {/* 選択中の情報 & 操作（仮配置なし時） */}
      {selectedTileType && !hasTemporary && (
        <div className="flex flex-col gap-2">
          <div className="text-xs text-center text-muted-foreground">
            {selectedTileType} (rotation: {selectedRotation})
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              className="btn-secondary text-xs py-1.5"
              onClick={onRotate}
            >
              Rotate
            </button>
            <button
              className="btn-ghost text-xs py-1.5"
              onClick={onClear}
            >
              Clear
            </button>
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Click hex to place. Click placed track to remove.
          </div>
        </div>
      )}

      {!selectedTileType && !hasTemporary && (
        <div className="text-xs text-muted-foreground">
          Select a tile type above, then click on a hex to place.
        </div>
      )}
    </div>
  );
}

export default PreLaidTrackPanel;
